// src/platform/storage/cacheManager.js

import indexedDb from '@/platform/storage/indexedDb'

import configuration from '@/platform/core/configuration'
import eventBus from '@/platform/core/eventBus'
import logger from '@/platform/core/logger'

class CacheManager {
  constructor() {
    this.storeName = 'cache'
  }

  async set(key, value, options = {}) {
    if (!key) {
      throw new Error(
        '[CacheManager] A cache key is required.'
      )
    }

    const now = Date.now()

    const defaultTtl =
      configuration.get(
        'cache.defaultTtl',
        null
      )

    const ttl =
      typeof options.ttl === 'number'
        ? options.ttl
        : defaultTtl

    const expiresAt =
      typeof ttl === 'number'
        ? now + ttl
        : null

    const existing =
      await indexedDb.get(
        this.storeName,
        key
      )

    const record = {
      key,
      value,

      createdAt:
        existing?.createdAt || now,

      updatedAt:
        now,

      expiresAt,

      metadata:
        options.metadata || null
    }

    await indexedDb.put(
      this.storeName,
      record
    )

    logger.debug(
      'Cache record stored.',
      {
        key,
        expiresAt
      }
    )

    eventBus.emit(
      'storage.cache.set',
      {
        key,
        expiresAt,
        metadata:
          record.metadata
      }
    )

    return record
  }

  async get(key, options = {}) {
    if (!key) {
      return null
    }

    const record =
      await indexedDb.get(
        this.storeName,
        key
      )

    if (!record) {
      eventBus.emit(
        'storage.cache.miss',
        {
          key
        }
      )

      return null
    }

    if (this.isExpired(record)) {
      await this.remove(
        key,
        {
          reason: 'expired'
        }
      )

      eventBus.emit(
        'storage.cache.expired',
        {
          key
        }
      )

      return null
    }

    eventBus.emit(
      'storage.cache.hit',
      {
        key
      }
    )

    if (options.withMetadata) {
      return {
        ...record
      }
    }

    return record.value
  }

  async has(key) {
    if (!key) {
      return false
    }

    const record =
      await indexedDb.get(
        this.storeName,
        key
      )

    if (!record) {
      return false
    }

    if (this.isExpired(record)) {
      await this.remove(
        key,
        {
          reason: 'expired'
        }
      )

      return false
    }

    return true
  }

  async remove(key, options = {}) {
    if (!key) {
      return false
    }

    await indexedDb.delete(
      this.storeName,
      key
    )

    logger.debug(
      'Cache record removed.',
      {
        key,
        reason:
          options.reason || 'manual'
      }
    )

    eventBus.emit(
      'storage.cache.removed',
      {
        key,
        reason:
          options.reason || 'manual'
      }
    )

    return true
  }

  async clear() {
    const count =
      await indexedDb.count(
        this.storeName
      )

    await indexedDb.clear(
      this.storeName
    )

    logger.info(
      'Cache cleared.',
      {
        count
      }
    )

    eventBus.emit(
      'storage.cache.cleared',
      {
        count
      }
    )

    return count
  }

  async getAll(options = {}) {
    const records =
      await indexedDb.getAll(
        this.storeName
      )

    const validRecords = []

    for (const record of records) {
      if (this.isExpired(record)) {
        await this.remove(
          record.key,
          {
            reason: 'expired'
          }
        )

        continue
      }

      validRecords.push(
        options.withMetadata
          ? { ...record }
          : {
              key:
                record.key,

              value:
                record.value
            }
      )
    }

    return validRecords
  }

  async removeExpired() {
    const records =
      await indexedDb.getAll(
        this.storeName
      )

    let removed = 0

    for (const record of records) {
      if (!this.isExpired(record)) {
        continue
      }

      await this.remove(
        record.key,
        {
          reason: 'expired'
        }
      )

      removed++
    }

    if (removed > 0) {
      logger.info(
        'Expired cache records removed.',
        {
          count: removed
        }
      )

      eventBus.emit(
        'storage.cache.expired.cleaned',
        {
          count: removed
        }
      )
    }

    return removed
  }

  isExpired(record) {
    if (
      record?.expiresAt === null ||
      record?.expiresAt === undefined
    ) {
      return false
    }

    return (
      Date.now() >=
      record.expiresAt
    )
  }

  async touch(key, ttl = null) {
    const resolvedTtl =
      typeof ttl === 'number'
        ? ttl
        : configuration.get(
            'cache.defaultTtl',
            null
          )

    if (
      typeof resolvedTtl !==
      'number'
    ) {
      throw new Error(
        '[CacheManager] touch() requires a TTL.'
      )
    }

    const record =
      await indexedDb.get(
        this.storeName,
        key
      )

    if (!record) {
      return false
    }

    const now =
      Date.now()

    record.updatedAt =
      now

    record.expiresAt =
      now + resolvedTtl

    await indexedDb.put(
      this.storeName,
      record
    )

    eventBus.emit(
      'storage.cache.touched',
      {
        key,
        expiresAt:
          record.expiresAt
      }
    )

    return true
  }

  async remember(
    key,
    factory,
    options = {}
  ) {
    const existing =
      await this.get(key)

    if (existing !== null) {
      return existing
    }

    if (
      typeof factory !==
      'function'
    ) {
      throw new Error(
        '[CacheManager] remember() requires a factory function.'
      )
    }

    try {
      const value =
        await factory()

      await this.set(
        key,
        value,
        options
      )

      return value
    } catch (error) {
      logger.error(
        'Cache factory failed.',
        {
          key,
          error
        }
      )

      eventBus.emit(
        'storage.cache.error',
        {
          operation:
            'remember',

          key,
          error
        }
      )

      throw error
    }
  }

  async getStats() {
    const records =
      await indexedDb.getAll(
        this.storeName
      )

    let active = 0
    let expired = 0

    for (const record of records) {
      if (this.isExpired(record)) {
        expired++
      } else {
        active++
      }
    }

    return {
      total:
        records.length,

      active,
      expired
    }
  }
}

const cacheManager =
  new CacheManager()

export default cacheManager